import React, { useState, useEffect } from "react";
import { useApi, getAccessToken } from "../apiHelpers";
import config from "../config";
import LogViewer from "./LogViewer";
import { useToast } from "../components/Toast/ToastContext";
import { buttonClass } from "../components/ui/Button";
import { FilePick } from "../components/ui/FilePick";
import { Tag, TagTone } from "../components/ui/Tag";
import FadeIn from "../components/ui/FadeIn";
import { EmptyState } from "../components/ui/EmptyState";
import { ListRow, ListStack } from "../components/ui/ListRow";
import { Skeleton, SkeletonGroup } from "../components/ui/Skeleton";
import type { ReportFinding, ReportRow, ReportChip } from "./types";

/**
 * DroneLogsSection - Shows flight logs for a drone
 * Logs are stored in Flight Review, CSV is cached for in-app viewing
 *
 * Props:
 * - droneSerial: string - Drone serial number
 * - sourceType: 'drone' | 'rma' | 'flag' - Filter by source
 * - sourceId: number - RMA ID or Flag ID (if sourceType is 'rma' or 'flag')
 * - showUpload: boolean - Whether to show upload button
 */
const SEVERITY_TONE: Record<string, TagTone> = { info: 'neutral', advisory: 'info', warning: 'warn', critical: 'bad' };

interface DroneLogsSectionProps {
    droneSerial: string;
    sourceType?: 'drone' | 'rma' | 'flag';
    sourceId?: number;
    showUpload?: boolean;
}

const COUNTS_LINE = /^(\d+) FAIL, (\d+) WARN, (\d+) PASS, (\d+) UNKNOWN/;

const findingLine = (f: ReportFinding) =>
    `${f.status} ${f.name}: ${f.value}${typeof f.t === 'number' ? ` at ${f.t.toFixed(1)} s` : ''}`;

/** One chip per log: the newest stored report whose summary carries the counts line. */
function reportChips(rows: ReportRow[]): Record<string, ReportChip> {
    const chips: Record<string, ReportChip> = {};
    for (const row of rows) {
        if (row.analysis_type !== 'programmatic' || !row.flight_review_log_id) continue;
        if (chips[row.flight_review_log_id]) continue;
        const m = COUNTS_LINE.exec((row.summary || '').split('\n')[0]);
        if (!m) continue;
        const fail = Number(m[1]);
        const warn = Number(m[2]);
        const findings = Array.isArray(row.findings) ? row.findings.filter(f => f && f.status) : [];
        chips[row.flight_review_log_id] = fail > 0
            ? { label: `${fail} FAIL`, hue: 'critical', title: findings.map(findingLine).join('\n') }
            : warn > 0
                ? { label: `${warn} WARN`, hue: 'warning', title: findings.map(findingLine).join('\n') }
                : { label: 'report ok', hue: 'info', title: 'No check failed or warned' };
    }
    return chips;
}

const DroneLogsSection: React.FC<DroneLogsSectionProps> = ({ droneSerial, sourceType = 'drone', sourceId, showUpload = true }) => {
    const [logs, setLogs] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [viewingLog, setViewingLog] = useState<any>(null);
    const [csvData, setCsvData] = useState<any>(null);
    const [loadingCsv, setLoadingCsv] = useState(false);
    const [chips, setChips] = useState<Record<string, ReportChip>>({});
    const api = useApi();
    const toast = useToast();

    useEffect(() => {
        fetchLogs();
    }, [droneSerial, sourceType, sourceId]);

    useEffect(() => {
        setChips({});
        if (!droneSerial) return;
        let current = true;
        api.get(`/analysis/results/drone/${droneSerial}`, { silent: true })
            .then((rows: ReportRow[]) => {
                if (current && Array.isArray(rows)) setChips(reportChips(rows));
            })
            .catch(() => {});
        return () => { current = false; };
    }, [droneSerial]);

    const fetchLogs = async () => {
        setLoading(true);
        try {
            let endpoint;
            if (sourceType === 'rma' && sourceId) {
                endpoint = `/logs/rma/${sourceId}`;
            } else if (sourceType === 'flag' && sourceId) {
                endpoint = `/logs/flag/${sourceId}`;
            } else {
                endpoint = `/logs/drone/${droneSerial}`;
            }

            const data = await api.get(endpoint);
            setLogs(data || []);
        } catch (err) {
            console.error('Error fetching logs:', err);
            setLogs([]);
        } finally {
            setLoading(false);
        }
    };

    const handleFileUpload = async (files: File[]) => {
        const file = files[0];
        if (!file) return;

        if (!file.name.toLowerCase().endsWith('.ulg')) {
            toast.warning('Only .ulg files are accepted');
            return;
        }

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('drone_serial', droneSerial);
            if (sourceType !== 'drone') {
                formData.append('source_type', sourceType);
                formData.append('source_id', String(sourceId));
            }

            const response = await fetch(`${config.API_ENDPOINT}/logs/upload`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${getAccessToken()}`
                },
                credentials: 'include',
                body: formData
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error?.message || 'Upload failed');
            }

            await fetchLogs();
        } catch (err: any) {
            console.error('Upload error:', err);
            toast.error(err.message || 'Failed to upload log file');
        } finally {
            setUploading(false);
        }
    };

    const viewLog = async (log: any) => {
        if (!log.csv_available) {
            toast.warning('Log data not available');
            return;
        }

        setLoadingCsv(true);
        try {
            const data = await api.get(`/logs/${log.id}/csv`);
            setCsvData(data.csv_data);
            setViewingLog({
                ...log,
                flight_review_url: data.flight_review_url || log.flight_review_url
            });
        } catch (err) {
            console.error('Error loading log:', err);
            toast.error('Failed to load log data. It may take a moment to process - please try again.');
        } finally {
            setLoadingCsv(false);
        }
    };

    const formatDate = (dateStr?: string | null) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatSize = (bytes?: number | null) => {
        if (!bytes) return '-';
        const mb = bytes / (1024 * 1024);
        return `${mb.toFixed(2)} MB`;
    };

    const formatDuration = (seconds?: number | null) => {
        if (!seconds) return null;
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <div>
            <div className="flex justify-between items-center mb-2">
                <h4 className="font-bold text-white text-sm">Flight Logs</h4>
                {showUpload && (
                    <FilePick
                        accept=".ulg"
                        onFiles={handleFileUpload}
                        disabled={uploading}
                        variant="tonal"
                        size="sm"
                    >
                        {uploading ? 'Uploading...' : '+ Upload'}
                    </FilePick>
                )}
            </div>

            <FadeIn key={loading ? 'loading' : logs.length === 0 ? 'empty' : 'list'}>
            {loading ? (
                <SkeletonGroup label="Loading flight logs" className="space-y-1">
                    <Skeleton className="h-12 w-full" />
                    <Skeleton className="h-12 w-full" />
                </SkeletonGroup>
            ) : logs.length === 0 ? (
                <EmptyState variant="area">No flight logs</EmptyState>
            ) : (
                <div className="max-h-48 overflow-y-auto">
                    <ListStack
                        items={logs}
                        getKey={(log: any) => String(log.id)}
                        on="panel"
                        label="Flight logs"
                        renderRow={(log: any) => (
                            <ListRow
                                title={<span className="font-mono text-xs">{log.original_filename}</span>}
                                meta={[
                                    formatDate(log.flight_date || log.uploaded_at),
                                    formatSize(log.file_size),
                                    log.duration_seconds ? formatDuration(log.duration_seconds) : null,
                                ].filter(Boolean).join(' • ')}
                                trailing={chips[log.flight_review_log_id] && (
                                    <Tag
                                        size="sm"
                                        tone={SEVERITY_TONE[chips[log.flight_review_log_id].hue]}
                                        className="shrink-0 font-semibold tabular-nums"
                                        title={chips[log.flight_review_log_id].title}
                                    >
                                        {chips[log.flight_review_log_id].label}
                                    </Tag>
                                )}
                                actions={(
                                    <button
                                        onClick={() => viewLog(log)}
                                        disabled={loadingCsv || !log.csv_available}
                                        className={buttonClass('quiet', 'sm')}
                                    >
                                        {loadingCsv ? '...' : log.csv_available ? 'View' : 'Not available'}
                                    </button>
                                )}
                            />
                        )}
                    />
                </div>
            )}
            </FadeIn>

            {/* Log Viewer Modal */}
            {viewingLog && csvData && (
                <LogViewer
                    csvData={csvData}
                    droneSerial={viewingLog.drone_serial}
                    flightDate={viewingLog.flight_date}
                    flightReviewUrl={viewingLog.flight_review_url}
                    onClose={() => {
                        setViewingLog(null);
                        setCsvData(null);
                    }}
                    onDelete={async () => {
                        if (!window.confirm('Delete this log entry? (The log will remain in Flight Review)')) {
                            return;
                        }
                        try {
                            await api.delete(`/logs/${viewingLog.id}`);
                            setViewingLog(null);
                            setCsvData(null);
                            await fetchLogs();
                        } catch (err) {
                            console.error('Delete error:', err);
                            toast.error('Failed to delete log');
                        }
                    }}
                />
            )}
        </div>
    );
};

export default DroneLogsSection;
